<script setup lang="ts">
import { onMounted } from 'vue'
import { NButton, useMessage } from 'naive-ui'
import ReviewQueueShell from './ReviewQueueShell.vue'
import SynthesisSuggestionCard from './SynthesisSuggestionCard.vue'
import { useSynthesisSuggestionsStore, type SynthesisKind } from '../stores/synthesis_suggestions'
import { useReviewQueueRun } from '../composables/useReviewQueueRun'

// Admin-only review queue for the monthly Wiki::Synthesis run. Shares the
// queue scaffold with /lint via ReviewQueueShell; this view
// supplies the synthesis store, the SynthesisSuggestionCard row, and the
// monthly-cadence copy. Auth gating happens at the router level — non-admins
// are bounced to /. The empty-state copy is calibrated to the monthly
// cadence so "no items" doesn't read as "something broke."

const store = useSynthesisSuggestionsStore()
const message = useMessage()

// "Run synthesis now" trigger — POSTs an ad-hoc SynthesisJob and
// locks the button until the run finishes. New suggestions stream into
// the queue via the store's channel subscription as the run lands them.
const run = useReviewQueueRun('synthesis_runs')
onMounted(() => void run.refresh())

async function runNow(): Promise<void> {
  try {
    await run.trigger()
    message.info('Synthesis run queued — new suggestions will appear here as they land.')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to queue synthesis run')
  }
}

// Kind buckets for the orientation line. Labels read as
// natural-language nouns rather than the underscore-cased enum.
const SYNTHESIS_KIND_LABELS: Record<SynthesisKind, string> = {
  concept_gap: 'concept gap',
  cross_page_pattern: 'cross-page pattern',
  emerging_theme: 'emerging theme',
  contradiction_reconciliation: 'contradiction reconciliation',
}
</script>

<template>
  <ReviewQueueShell
    :store="store"
    :items="store.suggestions"
    document-title="Synthesis review"
    heading="Synthesis review queue"
    :kind-labels="SYNTHESIS_KIND_LABELS"
    empty-description="No open synthesis suggestions — last month's run didn't surface anything, or every suggestion has been reviewed."
    list-testid="synthesis-list"
  >
    <template #actions>
      <NButton
        size="small"
        :loading="run.triggering.value"
        :disabled="run.running.value || run.triggering.value"
        data-testid="synthesis-run-now"
        @click="runNow"
        >{{ run.running.value ? 'Synthesis running…' : 'Run synthesis now' }}</NButton
      >
    </template>

    <template #description>
      Candidate new pages the monthly Synthesis pass drafted from cross-page patterns. Accept writes the drafted body to
      the proposed path (or appends a Synthesis section to an existing page). Edit refines the draft before accepting.
      Dismiss closes the row — the next monthly pass sees the dismissal and won't re-surface the same cluster.
    </template>

    <template #item="{ item }">
      <SynthesisSuggestionCard :suggestion="item" />
    </template>
  </ReviewQueueShell>
</template>
